import React, { useState, useEffect, useRef } from 'react';

const normaliseCompany = (company) => {
  if (!company) return null;
  return {
    ...company,
    companyName: company.companyName || company.company_name || company.name || '',
    webAddress: company.webAddress || company.web_address || company.website || '',
    phone: company.phone || company.phone_number || '',
    city: company.city || '',
    state: company.state || company.state_province || '',
  };
};

const UnmappedCompanies = ({ apiFetch, urlParams = {} }) => {
  const [resolveTargetId, setResolveTargetId] = useState(null);

  // Deep-link from notifier email. Carries company/contact/opportunity context.
  // action ∈ { create-company, link-company, link-contact }; falls back to legacy `create` + `autotask_id`.
  const deeplink = (() => {
    if (!urlParams) return null;
    const action = urlParams.action;
    if (!action) return null;
    const companyId = urlParams.company_id || urlParams.autotask_id;
    if (!['create-company', 'link-company', 'link-contact', 'create'].includes(action)) return null;
    return {
      action: action === 'create' ? 'create-company' : action,
      companyId: companyId ? String(companyId) : '',
      contactId: urlParams.contact_id ? String(urlParams.contact_id) : '',
      opportunityId: urlParams.opportunity_id ? String(urlParams.opportunity_id) : '',
      opportunityTitle: urlParams.opportunity_title || '',
      opportunityHubspotId: urlParams.opportunity_hubspot_id || '',
    };
  })();

  useEffect(() => {
    if (deeplink?.companyId) setResolveTargetId(deeplink.companyId);
  }, [deeplink?.companyId]);

  if (!resolveTargetId) return null;

  return (
    <ResolveCompanyModal
      autotaskId={resolveTargetId}
      context={deeplink && deeplink.companyId === resolveTargetId ? deeplink : null}
      apiFetch={apiFetch}
      onClose={() => setResolveTargetId(null)}
      onResolved={() => setResolveTargetId(null)}
    />
  );
};

export const ResolveCompanyModal = ({ autotaskId, context, apiFetch, onClose, onResolved, initialCompany = null }) => {
  const [atCompany, setAtCompany] = useState(() => normaliseCompany(initialCompany));
  const [atContact, setAtContact] = useState(null);
  const [atOpportunity, setAtOpportunity] = useState(null);
  const [hsResults, setHsResults] = useState([]);
  const [hsQuery, setHsQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false); // sticky: once a resolve succeeds, lock the buttons
  const inflightRef = useRef(false); // synchronous guard against double-clicks within the same tick
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [okMsg, setOkMsg] = useState('');
  const [existingMapping, setExistingMapping] = useState(null); // {hubspot_id, name} if already mapped

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const dialog = dialogRef.current;
    const firstControl = dialog?.querySelector('button, input, select, textarea, a[href]');
    firstControl?.focus();
    return () => previousFocusRef.current?.focus?.();
  }, []);

  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialogRef.current?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), '
      + 'textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    ) || []).filter((element) => element.getAttribute('aria-hidden') !== 'true');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // On mount: check the mapping table — if this AT company is already linked, show
  // a "resolved" banner instead of the create/link options.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/mappings/company-status?autotask_id=${encodeURIComponent(autotaskId)}`);
        const j = await r.json();
        if (!cancelled && j.success && j.mapped) {
          setExistingMapping({ hubspotId: j.hubspot_id, name: j.name });
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [autotaskId, apiFetch]);

  // Default which option (Create vs Link) is highlighted based on the email's action param.
  const initialMode = context?.action === 'link-company' || context?.action === 'link-contact' ? 'link' : 'create';

  // Load all referenced AT records in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      const seededCompany = normaliseCompany(initialCompany);
      if (seededCompany) {
        setAtCompany(seededCompany);
        if (seededCompany.companyName) setHsQuery(seededCompany.companyName);
      }

      // The quote audit already batch-loads company labels. Reuse that cached record
      // when supplied so opening this modal does not make another Autotask request.
      const tasks = [];
      if (!seededCompany) {
        tasks.push(apiFetch(`/autotask/companies/${autotaskId}`).then(r => r.json()).then(j => ['company', j]));
      }
      if (context?.contactId) tasks.push(apiFetch(`/autotask/contacts/${context.contactId}`).then(r => r.json()).then(j => ['contact', j]));
      if (context?.opportunityId) tasks.push(apiFetch(`/autotask/opportunities/${context.opportunityId}`).then(r => r.json()).then(j => ['opportunity', j]));
      try {
        const results = await Promise.all(tasks);
        if (cancelled) return;
        for (const [kind, j] of results) {
          if (!j?.success) continue;
          if (kind === 'company') {
            setAtCompany(j.company);
            if (j.company?.companyName) setHsQuery(j.company.companyName);
          }
          if (kind === 'contact') setAtContact(j.contact);
          if (kind === 'opportunity') setAtOpportunity(j.opportunity);
        }
      } catch (e) {
        if (!cancelled) setErrorMsg(e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [autotaskId, context?.contactId, context?.opportunityId, apiFetch, initialCompany]);

  const searchHubspot = async () => {
    if (!hsQuery.trim()) return;
    setBusy(true); setErrorMsg('');
    try {
      const r = await apiFetch(`/hubspot/companies/search?q=${encodeURIComponent(hsQuery.trim())}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Search failed');
      setHsResults(j.companies || []);
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const linkExisting = async (hubspotId, hubspotName, hubspotDomain) => {
    if (inflightRef.current || done) return;
    const atName = atCompany?.companyName || `AT ${autotaskId}`;
    const hsLabel = hubspotName ? `${hubspotName}${hubspotDomain ? ` (${hubspotDomain})` : ''}` : `HS ${hubspotId}`;
    const confirmed = window.confirm(
      `PERMANENT MAPPING — read carefully\n\n` +
      `You are about to link these two companies:\n\n` +
      `  Autotask:  ${atName}  (AT ${autotaskId})\n` +
      `  HubSpot:   ${hsLabel}  (HS ${hubspotId})\n\n` +
      `This is NOT a one-off link for the current opportunity.\n` +
      `Every future Autotask opportunity, quote, and contact created under ` +
      `${atName} will permanently auto-sync to ${hsLabel} from now on.\n\n` +
      `Only proceed if the Autotask company and the HubSpot company are the ` +
      `same real-world business. Wrong links create silent duplicates and are ` +
      `expensive to undo.\n\n` +
      `Click OK only if you are certain it is the same company.`
    );
    if (!confirmed) return;
    inflightRef.current = true;
    setBusy(true); setDone(true); setErrorMsg(''); setOkMsg('');
    try {
      const reqBody = { autotask_id: autotaskId, hubspot_id: String(hubspotId) };
      if (context?.opportunityId) reqBody.opportunity_id = context.opportunityId;
      if (context?.opportunityHubspotId) reqBody.opportunity_hubspot_id = context.opportunityHubspotId;
      if (context?.contactId) reqBody.contact_id = context.contactId;
      const r = await apiFetch('/mappings/link-company', {
        method: 'POST',
        body: JSON.stringify(reqBody),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Link failed');
      const verb = j.already_linked ? 'Already linked:' : 'Linked';
      const resyncSuffix = j.opportunity_resynced ? ` · opportunity ${context?.opportunityId || ''} resynced` : '';
      setOkMsg(`${verb} Autotask company ${autotaskId} ↔ HubSpot ${hubspotId}${resyncSuffix}`);
      setTimeout(onResolved, 1200);
    } catch (e) {
      setErrorMsg(e.message);
      // Keep `done` sticky so the user can't accidentally re-fire. They can close the
      // modal and reopen if they need to retry.
    } finally {
      setBusy(false);
      // Note: inflightRef stays true on purpose — once submitted, we don't allow re-fire.
    }
  };

  const createNew = async () => {
    if (inflightRef.current || done) return;
    inflightRef.current = true;
    setBusy(true); setDone(true); setErrorMsg(''); setOkMsg('');
    try {
      const reqBody = { autotask_id: autotaskId };
      // Forward opportunity / contact context so n8n can resync the originating opp.
      if (context?.opportunityId) reqBody.opportunity_id = context.opportunityId;
      if (context?.opportunityHubspotId) reqBody.opportunity_hubspot_id = context.opportunityHubspotId;
      if (context?.contactId) reqBody.contact_id = context.contactId;
      const r = await apiFetch('/mappings/create-hubspot-company-from-autotask', {
        method: 'POST',
        body: JSON.stringify(reqBody),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.error || 'Create failed');
      const verb = j.already_mapped ? 'Already mapped:' : 'Created HubSpot company';
      const resyncSuffix = j.opportunity_resynced ? ` · opportunity ${context?.opportunityId || ''} resynced` : '';
      setOkMsg(`${verb} ${j.hubspot_id} ↔ AT ${autotaskId}${resyncSuffix}`);
      setDone(true);
      setTimeout(onResolved, 1500);
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        ref={dialogRef}
        className="modal"
        style={{ maxWidth: 720 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`resolve-company-title-${autotaskId}`}
        tabIndex="-1"
        onKeyDown={handleDialogKeyDown}
      >
        <div className="modal-header">
          <h3 id={`resolve-company-title-${autotaskId}`}>Resolve Autotask company {autotaskId}</h3>
          <button onClick={onClose} className="modal-close" aria-label="Close company review">×</button>
        </div>
        <div className="modal-body">
          {errorMsg && <div style={{ background: '#fee', color: '#900', padding: 8, borderRadius: 4, marginBottom: 8 }}>{errorMsg}</div>}
          {okMsg && <div style={{ background: '#efe', color: '#070', padding: 8, borderRadius: 4, marginBottom: 8 }}>{okMsg}</div>}
          {existingMapping && !okMsg && (
            <div style={{ background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#047857', fontWeight: 700, marginBottom: 4 }}>✓ Already resolved</div>
              <div style={{ fontSize: 13, color: '#065f46' }}>
                Linked to HubSpot company <strong>{existingMapping.name || '—'}</strong> · HS id <code>{existingMapping.hubspotId}</code>
              </div>
              <a href={`https://app.hubspot.com/contacts/23367316/company/${existingMapping.hubspotId}`} target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: '#1f6feb' }}>open company in HubSpot →</a>
              {context?.opportunityId && (
                <div style={{ marginTop: 10 }}>
                  <button className="btn btn-primary" disabled={busy || done} onClick={createNew}>
                    Resync opportunity {context.opportunityId} →
                  </button>
                  <span style={{ fontSize: 12, color: '#065f46', marginLeft: 10 }}>(Re-fires the deal sync; safe to repeat.)</span>
                </div>
              )}
            </div>
          )}

          {context?.opportunityId && (
            <div style={{ marginBottom: 12, padding: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#9a3412', fontWeight: 600, marginBottom: 6 }}>Originating opportunity (quote)</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{atOpportunity?.title || context.opportunityTitle || '(no title)'}</div>
              <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
                AT id <code>{context.opportunityId}</code>
                {atOpportunity?.amount != null && <> · amount ${atOpportunity.amount}</>}
                {atOpportunity?.status != null && <> · status {atOpportunity.status}</>}
                {context.opportunityHubspotId && <> · HS deal <code>{context.opportunityHubspotId}</code></>}
              </div>
              <a href={`https://ww29.autotask.net/Mvc/CRM/OpportunityDetail.mvc?ids%5B0%5D=${context.opportunityId}&OpportunityId=${context.opportunityId}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1f6feb' }}>open in Autotask →</a>
              {context.opportunityHubspotId && (
                <> · <a href={`https://app.hubspot.com/contacts/23367316/deal/${context.opportunityHubspotId}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1f6feb' }}>open deal in HubSpot →</a></>
              )}
            </div>
          )}

          {context?.contactId && (
            <div style={{ marginBottom: 12, padding: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#3730a3', fontWeight: 600, marginBottom: 6 }}>Triggered by contact</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>
                {atContact ? [atContact.firstName, atContact.lastName].filter(Boolean).join(' ') : '(loading…)'}
              </div>
              <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
                {atContact?.emailAddress || '—'} · AT id <code>{context.contactId}</code>
              </div>
              <a href={`https://ww29.autotask.net/Mvc/CRM/ContactDetail.mvc?ids%5B0%5D=${context.contactId}&ContactId=${context.contactId}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1f6feb' }}>open in Autotask →</a>
            </div>
          )}

          <div style={{ marginBottom: 16, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', fontWeight: 600, marginBottom: 6 }}>Autotask company {initialMode === 'create' ? '(needs HubSpot)' : ''}</div>
            {atCompany ? (
              <>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{atCompany.companyName}</div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                  {[atCompany.webAddress, atCompany.phone, atCompany.city, atCompany.state].filter(Boolean).join(' · ') || '—'}
                </div>
                <a
                  href={`https://ww29.autotask.net/Mvc/CRM/AccountDetail.mvc?ids%5B0%5D=${autotaskId}&AccountId=${autotaskId}`}
                  target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: '#1f6feb' }}
                >open in Autotask →</a>
              </>
            ) : (
              <div style={{ color: '#6b7280' }}>{busy ? 'Loading…' : 'Could not load Autotask company.'}</div>
            )}
          </div>

          {!existingMapping && (
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Option A — Create a new HubSpot company</h4>
              <button className="btn btn-primary" disabled={busy || done || !atCompany} onClick={createNew}>
                + Create in HubSpot from this AT record
              </button>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Creates a new company in HubSpot using the AT data above and writes the mapping row.</div>
            </div>
          )}

          {!existingMapping && (
            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14 }}>Option B — Link to an existing HubSpot company</h4>

              <div style={{
                background: '#fef2f2',
                border: '2px solid #f87171',
                borderRadius: 6,
                padding: 12,
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#991b1b', fontWeight: 700, letterSpacing: '.04em', marginBottom: 6 }}>
                  ⚠ Linking is permanent and applies to every future record
                </div>
                <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 }}>
                  This is <strong>not</strong> a one-off link for the current opportunity.
                  Once linked, <strong>every future Autotask opportunity, quote, and contact</strong> created under
                  {atCompany?.companyName ? <> <strong>{atCompany.companyName}</strong></> : <> this Autotask company</>} will
                  automatically sync into the HubSpot company you choose below — forever, until manually unmapped.
                  <br /><br />
                  Only link if the Autotask company and the HubSpot company are the <strong>same real-world business</strong>.
                  Wrong links create silent duplicates that are expensive to undo (manual DB edits + HubSpot/Autotask cleanup).
                  If unsure, close this dialog — the current opportunity will still sync to HubSpot, just without a company association,
                  and you can resolve the mapping properly later.
                </div>
              </div>

              <label
                htmlFor={`hubspot-company-search-${autotaskId}`}
                style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5 }}
              >
                Search HubSpot by company name or domain
              </label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  id={`hubspot-company-search-${autotaskId}`}
                  type="text"
                  value={hsQuery}
                  onChange={(e) => setHsQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchHubspot()}
                  placeholder="Search HubSpot by name or domain…"
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 4 }}
                />
                <button className="btn btn-secondary" onClick={searchHubspot} disabled={busy || done}>
                  {busy ? 'Searching…' : 'Search'}
                </button>
              </div>
              {hsResults.length > 0 && (
                <table className="data-table" style={{ marginTop: 4 }}>
                  <thead><tr><th>Name</th><th>Domain</th><th>HS ID</th><th></th></tr></thead>
                  <tbody>
                    {hsResults.map((c) => (
                      <tr key={c.id}>
                        <td>{c.properties?.name || '-'}</td>
                        <td>{c.properties?.domain || '-'}</td>
                        <td><code>{c.id}</code></td>
                        <td>
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={busy || done}
                            onClick={() => linkExisting(c.id, c.properties?.name, c.properties?.domain)}
                            title="Permanent: all future opps/quotes/contacts under this AT company will sync to this HubSpot company"
                          >
                            Link permanently
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {hsResults.length === 0 && !busy && hsQuery && (
                <div style={{ fontSize: 13, color: '#6b7280' }}>No HubSpot companies match — try a different name, or create new.</div>
              )}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                Linking writes a mapping row directly (no new HubSpot company is created). Use this only when
                you have verified the AT company already exists in HubSpot as the same real-world business
                under a different HubSpot id. The link affects all future sync activity for this AT company, not just the current deal.
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default UnmappedCompanies;
